import { useCallback, useEffect, useState, Suspense, lazy } from 'react';
import { PreparedTaskType } from '../types';
import styles from './styles.module.scss';
import { Checkbox } from 'antd';
import { Table } from 'components-antd';
import { Icons } from 'pages/Profile/components/icons';
import { isBeforeToday, isToday, isYesterday } from 'helpers/date';
import { addSpaceBeforeCapitalLetters, getDateOnly } from 'helpers';
import classNames from 'classnames';
import { mapTasksStatuses, tasksStatusesIds } from 'settings/constants/transactionTasks';
import { useDispatch } from 'react-redux';
import { Spinner } from 'components';
import {
  getTransactionEffect,
  getTransactionTaskEffect,
  resetTransactionTaskEffect,
  setTransactionTaskParamsEffect,
  setTransactionTasksLastStateEffect,
} from 'store/effects';

const TaskViewRevamped = lazy(
  () =>
    import(
      'pages/Workshop/Transactions/TransactionTasks/components/Task/ViewRevamped/ViewRevamped'
    ),
);

type TasksTablePropsType = {
  tasks: PreparedTaskType[];
  multiSelect: boolean;
  searchQuery: string;
  selectedTasksIds: number[];
  setSelectedTasksIds: Function;
  allSelected: boolean;
  setAllSelected: Function;
};

const TasksTable = (props: TasksTablePropsType) => {
  const {
    tasks,
    multiSelect,
    searchQuery,
    selectedTasksIds,
    setSelectedTasksIds,
    allSelected,
    setAllSelected,
  } = props;
  const dispatch = useDispatch();
  const [internalTasks, setInternalTasks] = useState<PreparedTaskType[]>(tasks);
  const [showTaskViewModal, setShowTaskViewModal] = useState<boolean>(false);

  useEffect(() => {
    setInternalTasks(tasks);
  }, [tasks.length]);

  useEffect(() => {
    handleSearchQuery();
  }, [searchQuery]);

  const handleSearchQuery = () => {
    const trimmedSearchQuery = searchQuery.trim();
    if (trimmedSearchQuery) {
      const filteredTasks = tasks.filter(
        (task) => task.Description?.includes(searchQuery) || task.Title?.includes(searchQuery),
      );
      setInternalTasks(filteredTasks);
    } else setInternalTasks(tasks);
  };

  const handleCheckboxChange = (e, task) => {
    const isChecked = e.target.checked;
    if (isChecked && !selectedTasksIds.includes(task.Id))
      setSelectedTasksIds((prev) => [...prev, task.Id]);
    else if (!isChecked && selectedTasksIds.includes(task.Id)) {
      if (allSelected) setAllSelected(false);
      setSelectedTasksIds((prev) => prev.filter((id) => id !== task.Id));
    }
  };

  const handleMainCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setAllSelected(isChecked);

    if (isChecked) {
      const allTasksIds = internalTasks.map((task) => task.Id);
      setSelectedTasksIds(allTasksIds);
    } else setSelectedTasksIds([]);
  };

  const getStatusClassName = useCallback((val) => {
    switch (val) {
      case tasksStatusesIds.new:
        return styles.new;
      case tasksStatusesIds.inProgress:
        return styles.inProgress;
      case tasksStatusesIds.stuck:
        return styles.stuck;
      case tasksStatusesIds.done:
        return styles.done;
      case tasksStatusesIds.overdue:
        return styles.overdue;
      case tasksStatusesIds.na:
        return styles.na;
      default:
        return '';
    }
  }, []);

  const getColumns = (): any[] => {
    const columns: any[] = [
      {
        title: 'Task',
        key: 'Title',
        dataIndex: 'Title',
        width: multiSelect ? 570 : 500,
        sorter: (a, b) => {
          if (a.Title < b.Title) return -1;
          if (a.Title > b.Title) return 1;
          return 0;
        },
        render: (_, row) => {
          return (
            <div className={styles.task}>
              <div className={styles.left}>
                <Icons variant={Icons.TASK} />
              </div>
              <div className={styles.right}>
                <div className={styles.title}>{row.Title}</div>
                {row.TransactionName && (
                  <div className={styles.description}>{row.TransactionName}</div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Status',
        key: 'Status',
        dataIndex: 'Status',
        sorter: (a, b) => {
          if (a.Status < b.Status) return -1;
          if (a.Status > b.Status) return 1;
          return 0;
        },
        width: 170,
        render: (_, { Status }) => {
          return (
            <div className={classNames(styles.status, getStatusClassName(Status))}>
              {mapTasksStatuses[Status]}
            </div>
          );
        },
      },
      {
        title: 'Due Date',
        key: 'DueDate',
        dataIndex: 'DueDate',
        sorter: (a, b) => {
          if (a.DueDate < b.DueDate) return -1;
          if (a.DueDate > b.DueDate) return 1;
          return 0;
        },
        width: 200,
        render: (_, { DueDate, Status }) => {
          let dueDate = DueDate ? getDateOnly(DueDate, 'MM/DD/YYYY') : 'N/A';
          let isOverdue = false;

          if (isToday(dueDate)) {
            dueDate = 'Today';
            isOverdue = true;
          }
          if (isYesterday(dueDate)) {
            dueDate = 'Yesterday';
            isOverdue = true;
          }
          if (isBeforeToday(dueDate)) {
            isOverdue = true;
          }

          return (
            <div
              className={classNames(styles.dueDate, {
                [styles.overdueDate]: isOverdue && Status === 'Overdue',
              })}
            >
              {dueDate}
            </div>
          );
        },
      },
      {
        title: 'Type',
        key: 'Type',
        dataIndex: 'Type',
        sorter: (a, b) => {
          if (a.Type < b.Type) return -1;
          if (a.Type > b.Type) return 1;
          return 0;
        },
        width: 170,
        render: (_, { Type, SubType }) => {
          return (
            <div className={styles.category}>
              <div className={styles.type}>{Type}</div>
              {SubType && (
                <div className={styles.subType}>{addSpaceBeforeCapitalLetters(SubType ?? '')}</div>
              )}
            </div>
          );
        },
      },
    ];

    if (multiSelect) {
      columns.push({
        title: ({ sortOrder, sortColumn, filters }) => {
          return (
            <Checkbox
              type="checkbox"
              name="isPrimary"
              className="mosaikCheckbox"
              value={allSelected}
              onChange={(e) => handleMainCheckboxChange(e)}
              checked={allSelected}
            />
          );
        },
        width: 50,
        render: (_, row) => {
          return (
            <Checkbox
              type="checkbox"
              name="isPrimary"
              className="mosaikCheckbox"
              value={selectedTasksIds.includes(row.Id)}
              onChange={(e) => handleCheckboxChange(e, row)}
              checked={selectedTasksIds.includes(row.Id)}
            />
          );
        },
      });
    }

    return columns;
  };

  const handleTaskViewClick = (task) => {
    const { Transaction: transaction, Id: taskId, CampaignId: campaignId } = task;
    const transactionId = transaction?.Id;
    transactionId &&
      dispatch(getTransactionEffect({ id: transactionId, include: 'tasks,participants' }));
    dispatch(
      setTransactionTaskParamsEffect({
        taskId,
        transactionId,
        campaignId,
        isTaskDetail: true,
      }),
    );
    dispatch(getTransactionTaskEffect({ transactionId, taskId }));
    dispatch(setTransactionTasksLastStateEffect({ taskId }));
    setShowTaskViewModal(true);
  };

  const onTaskViewCloseModal = () => {
    setShowTaskViewModal(false);
    dispatch(
      setTransactionTaskParamsEffect({ campaignId: null, isTaskForm: false, isTaskDetail: false }),
    );
    dispatch(resetTransactionTaskEffect());
  };

  if (!internalTasks.length)
    return (
      <div className={styles.tableContent}>
        <div className={styles.empty}>No tasks to show.</div>
      </div>
    );

  return (
    <div className={styles.tableContent}>
      <Suspense fallback={<Spinner />}>
        <TaskViewRevamped
          isOpen={showTaskViewModal}
          onCloseModal={onTaskViewCloseModal}
          className=""
        />
      </Suspense>
      <Table
        className={classNames(styles.tasksTable, { [styles.tasksTableMulti]: multiSelect })}
        bordered={false}
        columns={getColumns()}
        dataSource={internalTasks.map((item, key) => ({
          ...item,
          key,
        }))}
        pagination={false}
        onRow={(record) => ({
          onClick: (e: any) => {
            if (e?.target?.className === 'ant-checkbox-input') return;

            handleTaskViewClick(record);
          },
        })}
      />
    </div>
  );
};

export default TasksTable;
